import gql from 'graphql-tag';
import { colonyAPI } from '@/api';

import channelUnlinkMutation from '@/graphql/unlinkChannel.gql';

import * as types from './mutation-types';

export default {
  createCampaign({ commit, dispatch }, campaignData, advertiserData, channelData) {
    this.$apollo.mutate({
      mutation: gql`mutation {
        createCampaign: {
          title: $campaignTitle,
          startDate: $campaignStartDate,
          endDate: $campaignEndDate,
          live: true,
          default: $campaignIsDefault,
        },
        newAdvertiser: {
          advertiser: $advertiserTitle,
          leadsGenerated: $advertiserLeadsGenerated,
          prizeQuantity: $advertiserPrizeQuantity,
          prizeSpend: $advertiserPrizeSpend,
          prizeVolume: $advertiserPrizeVolume,
          salesGenerated: $advertiserSalesGenerated,
          category: $advertiserCategory,
          prizeCategory: $advertiserPrizeCategory,
        },
        newChannelLinks: {
          keyword: $channelKeyword,
          channelIds: $channelIds,
        }
      }`,
      variables: {
        campaignTitle: campaignData.title,
        campaignStartDate: campaignData.startDate,
        campaignEndDate: campaignData.endDate,
        campaignIsDefault: campaignData.default,
        winningDisqualificationDuration: campaignData.winningDisqualificationDuration,

        advertiseerTitle: advertiserData.title,
        advertiserLeadsGenerated: advertiserData.leadsGenerated,
        advertiserPrizeQuantity: advertiserData.prizeQuantity,
        advertiserPrizeSpend: advertiserData.prizeSpend,
        advertiserPrizeVolume: advertiserData.prizeVolume,
        advertiserSalesGenerated: advertiserData.salesGenerated,
        advertiserCategory: advertiserData.category,
        advertiserPrizeCategory: advertiserData.prizeCategory,

        channelKeyword: channelData.keyword,
        channelIds: channelData.channelIds,
      },
    });
  },

  getCampaigns({ commit }) {
    commit(types.GET_CAMPAIGNS_REQUEST);
    colonyAPI
      .get('/campaigns/', { params: { cache: 60000 } })
      .then(response => {
        // dispatch('getReplyMessages');
        commit(types.GET_CAMPAIGNS_SUCCESS, response);
      })
      .catch(error => commit(types.GET_CAMPAIGNS_FAILURE, error));
  },

  getReplyMessages({ commit }) {
    commit(types.GET_REPLY_MESSAGES_REQUEST);
    colonyAPI
      .get('/replymessages/')
      .then(response => commit(types.GET_REPLY_MESSAGES_SUCCESS, response))
      .catch(error => commit(types.GET_REPLY_MESSAGES_FAILURE, error.response));
  },

  /**
   * Get campaign detail.
   *
   * fetches campaign detail required on the campaign detail page.
   *
   * @param {any} { commit }
   * @param {any} id
   * @returns Promise
   */
  getCampaignDetail({ commit }, id) {
    commit(types.GET_CAMPAIGN_DETAIL_REQUEST, id);
    return colonyAPI
      .get(`/campaigns/linked/channels/${id}/`)
      .then(response => commit(types.GET_CAMPAIGN_DETAIL_SUCCESS, response.data))
      .catch(() => commit(types.GET_CAMPAIGN_DETAIL_FAILURE, id));
  },

  /**
   * Download messages.
   *
   * Calls the download messages api on colony core,
   * send a notification to the user indicating success
   * or error.
   *
   * @param {any} { commit }
   * @param {any} payload
   * @returns Promise
   */
  downloadMessages({ commit, dispatch }, payload) {
    commit(types.CREATE_CAMPAIGN_MESSAGES_REQUEST);
    return colonyAPI
      .post(`/campaigns/${payload.campaign_id}/downloadmessages/`, payload)
      .then(response => {
        dispatch('toast/success', 'Your download message request has successfully been queued.', {
          root: true,
        });
        commit(types.CREATE_CAMPAIGN_MESSAGES_SUCCESS);
        return Promise.resolve(response);
      })
      .catch(error => {
        dispatch('toast/error', `Unable to create your request for download messages. ${error}`, {
          root: true,
        });
        commit(types.CREATE_CAMPAIGN_MESSAGES_FAILURE);
        return Promise.reject(error);
      });
  },

  /**
   * Download Bot report.
   *
   * Calls the bot reports api on colony core,
   * send a notification to the user indicating success
   * or error.
   *
   * @param {any} { commit }
   * @param {any} payload
   * @returns Promise
   */
  downloadBotReport({ commit, dispatch }, payload) {
    commit(types.CREATE_CAMPAIGN_MESSAGES_REQUEST);
    return colonyAPI
      .post(`/campaigns/${payload.campaign_id}/downloadbotreport/`, payload)
      .then(response => {
        dispatch('toast/success',
        'Your request has been successfully received. You will be notified when the file is ready to download.', {
          root: true,
        });
        commit(types.CREATE_CAMPAIGN_MESSAGES_SUCCESS);
        return Promise.resolve(response);
      })
      .catch(error => {
        dispatch('toast/error', `Unable to create your request for generating report. ${error}`, {
          root: true,
        });
        commit(types.CREATE_CAMPAIGN_MESSAGES_FAILURE);
        return Promise.reject(error);
      });
  },

  /**
   * Run random selection.
   *
   * Calls the random selection api on colony core,
   * and send a notification to the user indicating if
   * it was successful or failed.
   *
   * @param {any} { commit, dispatch }
   * @param {any} payload
   * @returns Promise
   */
  runRandomSelector({ commit, dispatch }, payload) {
    commit(types.CREATE_RANDOM_SELECTION_REQUEST);
    return colonyAPI
      .post('/randomselector/', payload)
      .then(response => {
        dispatch('toast/success', 'Your random selection has successfully been queued.', {
          root: true,
        });
        commit(types.CREATE_RANDOM_SELECTION_SUCCESS);
        return Promise.resolve(response);
      })
      .catch(error => {
        dispatch('toast/error', `Unable to create your random selection. ${error}`, {
          root: true,
        });
        commit(types.CREATE_RANDOM_SELECTION_FAILURE);
        return Promise.reject(error);
      });
  },
  /**
   * Gets random selections .
   *
   * This will return all the random selections made
   *
   * @param {any} { commit }
   * @param {any} id
   * @returns Promise
   */
  getRandomSelections({ commit }, id) {
    commit(types.GET_RANDOM_SELECTIONS_REQUEST, id);

    return colonyAPI
      .get(`/randomselector/${id}/`)
      .then(response => commit(types.GET_RANDOM_SELECTIONS_SUCCESS, response.data))
      .catch(() => commit(types.GET_RANDOM_SELECTIONS_FAILURE, id));
  },

  getRandomSelectionsWinner({ commit, dispatch }, selection) {
    commit(types.SET_RANDOM_SELECTIONS_WINNER_REQUEST, selection);
    return colonyAPI
      .get(`/randomselector/winner/${selection.id}/`)
      .then(response => {
        dispatch('toast/success', 'Your winner has been successfully selected', {
          root: true,
        });
        commit(types.SET_RANDOM_SELECTIONS_WINNER_SUCCESS, response.data);
      })
      .catch(() => commit(types.SET_RANDOM_SELECTIONS_WINNER_FAILURE, selection.selection));
  },

  unlinkChannel({ commit }, linkId) {
    this.$apollo.mutate({
      mutation: channelUnlinkMutation,
      variables: {
        linkId,
      },
    });
  },

  setCamapignError({ commit, error }) {
    commit(types.GET_CAMPAIGNS_FAILURE, error);
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/campaign/actions.js